import React from "react";
import { Box, Typography, Chip, Stack } from "@mui/material";

const getPriorityStyles = (priority) => {
  switch (priority) {
    case "High":
      return {
        borderColor: "#C10007",
        chipBg: "#FEF2F2",
        chipColor: "#C10007",
        chipBorder: "1px solid #C10007",
      };
    case "Medium":
      return {
        borderColor: "#E17100",
        chipBg: "#FEF3C6",
        chipColor: "#E17100",
        chipBorder: "1px solid #E17100",
      };
    case "Low":
      return {
        borderColor: "#00A63E",
        chipBg: "#DCFCE7",
        chipColor: "#00A63E",
        chipBorder: "1px solid #00A63E",
      };
    default:
      return {
        borderColor: "#E5E7EB",
        chipBg: "#F3F4F6",
        chipColor: "#374151",
        chipBorder: "1px solid #374151",
      };
  }
};

const WorkOrderCard = ({ workOrder }) => {
  const priorityStyles = getPriorityStyles(workOrder.priority);

  return (
    <Box
      sx={{
        background: "#FFFFFF",
        border: `1px solid ${priorityStyles.borderColor}`,
        borderRadius: "12px",
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
      }}
    >
      {/*  Title + Priority chip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {/* Title + Location breadcrumb */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {/* Top row: ID chip */}
          <Box>
            <Chip
              label={workOrder.id}
              sx={{
                bgcolor: "#45556C",
                color: "#fff",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "24px",
                borderRadius: "8px",
                flexShrink: 0,
                fontFamily:
                  'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              }}
            />
          </Box>
          {/* Title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "24px",
                color: "#020618",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {workOrder.title}
            </Typography>
          </Box>
          {/* Location breadcrumb */}
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "20px",
              color: "#62748E",
            }}
          >
            {workOrder.location}
          </Typography>
        </Box>

        {/* Right: Priority chip */}
        <Chip
          label={workOrder.priority}
          sx={{
            bgcolor: priorityStyles.chipBg,
            color: priorityStyles.chipColor,
            border: priorityStyles.chipBorder,
            fontWeight: 600,
            fontSize: "12px",
            borderRadius: 0.6,
            height: "20px",
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Description */}
      <Typography
        sx={{
          fontSize: "14px",
          lineHeight: "20px",
          fontWeight: 400,
          color: "#020618",
        }}
      >
        {workOrder.description}
      </Typography>

      {/* Details row: Technician, Due, Cost, Parts */}
      <Stack direction="row" spacing={4} sx={{ mt: 0.5 }}>
        {[
          { label: "Technician", value: workOrder.technician },
          { label: "Due", value: workOrder.dueDate },
          {
            label: "Cost",
            value: `$${workOrder.cost.toLocaleString()}`,
          },
          { label: "Parts", value: `${workOrder.parts} items` },
        ].map((detail) => (
          <Box key={detail.label} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: "#020618",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "24px",
                mb: 1,
              }}
            >
              {detail.label}
            </Typography>
            <Typography
              sx={{
                color: "#020618",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "24px",
              }}
            >
              {detail.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default WorkOrderCard;
